import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { DownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';
import { showDownloadStartToast, showVersionMismatchToast } from '@/utils/toastHelper';
import { sendDownloadClickEvent, sendWeblogEvent } from '@/utils/weblogger';

import styles from './DownloadSection.module.less';
import appleImg from './apple.svg';
import arrowPng from './arrow.png';
import bgImg from './bg.png';
import windowImg from './window.svg';

interface DownloadSectionProps {
  downloadInfo: {
    arm?: DownloadInfo;
    intel?: DownloadInfo;
    windows?: DownloadInfo;
  } | null;
  loading: boolean;
  defaultMacType: 'arm' | 'intel';
  userSystem: 'apple' | 'intel' | 'windows' | null;
}

/**
 * DownloadSection 组件
 * 根据设计稿重新实现的下载区域组件
 */
const DownloadSection: React.FC<DownloadSectionProps> = ({
  downloadInfo,
  loading,
  defaultMacType,
  userSystem,
}) => {
  const { t } = useTranslation();
  const [selectedMacType, setSelectedMacType] = useState<'arm' | 'intel'>(defaultMacType);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // 当默认Mac类型改变时更新选中状态
  useEffect(() => {
    setSelectedMacType(defaultMacType);
  }, [defaultMacType]);

  // 计算下拉菜单位置 - 根据屏幕尺寸动态调整位置和大小
  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // 根据屏幕尺寸确定下拉菜单宽度
      let dropdownWidth = 322;
      if (viewportWidth <= 479) {
        // 小屏手机（≤479px）
        dropdownWidth = 260;
      } else if (viewportWidth <= 767) {
        // 普通移动端（≤767px）
        dropdownWidth = 280;
      }
      // 桌面端保持 322px

      // 计算理想的 left 位置（优先右对齐到按钮）
      const idealLeft = rect.right - dropdownWidth;

      // 确保下拉菜单不会超出屏幕左边界
      const minLeft = 16; // 最小左边距
      const actualLeft = Math.max(idealLeft, minLeft);

      // 确保下拉菜单不会超出屏幕右边界
      const maxLeft = viewportWidth - dropdownWidth - 16; // 最大左边距（保留16px右边距）
      const finalLeft = Math.min(actualLeft, maxLeft);

      setDropdownPosition({
        top: rect.bottom + 8,
        left: finalLeft,
        width: dropdownWidth,
      });
    }
  };

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDropdown(false);
      }
    };

    const handleScroll = () => {
      if (showDropdown) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showDropdown]);

  // 当显示下拉菜单时更新位置
  useEffect(() => {
    if (showDropdown) {
      updateDropdownPosition();
    }
  }, [showDropdown]);

  // 处理下载
  const handleDownload = (type: 'arm' | 'intel' | 'windows') => {
    if (!downloadInfo) return;

    let downloadUrl = '';
    let version = '';
    let chipType = '';
    let platformName = '';

    if (type === 'arm' && downloadInfo.arm?.url) {
      downloadUrl = downloadInfo.arm.url;
      version = downloadInfo.arm.version;
      chipType = t('download.architectures.appleChip');
      platformName = 'apple';
    } else if (type === 'intel' && downloadInfo.intel?.url) {
      downloadUrl = downloadInfo.intel.url;
      version = downloadInfo.intel.version;
      chipType = t('download.architectures.intelChip');
      platformName = 'intel';
    } else if (type === 'windows' && downloadInfo.windows?.url) {
      downloadUrl = downloadInfo.windows.url;
      version = downloadInfo.windows.version;
      chipType = t('download.platforms.windows');
      platformName = 'windows';
    }

    if (downloadUrl) {
      // 检查版本是否与用户系统匹配
      let isMismatch = false;
      let currentSystemName = '';
      let downloadSystemName = '';

      if (userSystem) {
        // 判断是否匹配
        if (userSystem === 'apple' && type !== 'arm') {
          isMismatch = true;
          currentSystemName = t('download.architectures.appleSilicon');
          downloadSystemName =
            type === 'intel' ? t('download.architectures.intel') : t('download.platforms.windows');
        } else if (userSystem === 'intel' && type !== 'intel') {
          isMismatch = true;
          currentSystemName = t('download.architectures.intel');
          downloadSystemName =
            type === 'arm'
              ? t('download.architectures.appleSilicon')
              : t('download.platforms.windows');
        } else if (userSystem === 'windows' && type !== 'windows') {
          isMismatch = true;
          currentSystemName = t('download.platforms.windows');
          downloadSystemName =
            type === 'arm'
              ? t('download.architectures.appleSilicon')
              : t('download.architectures.intel');
        }

        // 如果版本不匹配，显示警告提醒
        if (isMismatch) {
          showVersionMismatchToast(currentSystemName, downloadSystemName);
        }
      }

      // 埋点上报：Download页面下载按钮点击
      sendDownloadClickEvent(platformName, version || 'unknown', 'download');

      // 显示开始下载的 toast 提示
      showDownloadStartToast(chipType, version);

      // 触发下载
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 获取当前选中的Mac版本信息
  const getCurrentMacInfo = () => {
    return selectedMacType === 'arm' ? downloadInfo?.arm : downloadInfo?.intel;
  };

  return (
    <div className={styles.downloadSection}>
      <div className={styles.contentContainer}>
        {/* 标题区域 */}
        <div className={styles.downloadHeader}>
          <h1 className={styles.downloadTitle}>
            <span className={styles.titleHighlight}>{t('download.title')}</span>
          </h1>
          <p className={styles.downloadSubtitle}>{t('download.subtitle')}</p>
        </div>

        {/* 下载卡片区域 */}
        <div className={styles.downloadCardsContainer}>
          {/* macOS 卡片 */}
          <div className={`${styles.downloadCard} ${styles.macosCard}`}>
            {/* 背景图 */}
            <div className={styles.cardBackground}>
              <img src={bgImg} alt="macOS background" className={styles.backgroundImage} />
            </div>

            {/* 卡片内容 */}
            <div className={styles.cardContentWrapper}>
              {/* 平台信息 */}
              <div className={styles.platformSection}>
                <div className={styles.platformIconWrapper}>
                  <img src={appleImg} alt="macOS icon" className={styles.platformIcon} />
                </div>
                <div className={styles.platformInfo}>
                  <h3 className={styles.platformTitle}>{t('download.platforms.macos')}</h3>
                  <p className={styles.platformRequirement}>
                    {t('download.platforms.requirement')}
                  </p>
                </div>
              </div>

              {/* 下载按钮组 */}
              <div className={styles.downloadButtonGroup} ref={buttonRef}>
                {/* 统一的macOS下载按钮 */}
                <button
                  className={`${styles.downloadButton} ${styles.macos} ${styles.unifiedButton}`}
                  onClick={() => setShowDropdown(!showDropdown)}
                  disabled={loading}
                >
                  <span className={styles.buttonText}>
                    {t('download.buttons.downloadFor', {
                      platform:
                        selectedMacType === 'arm'
                          ? t('download.architectures.appleSilicon')
                          : t('download.architectures.intel'),
                    })}
                    &nbsp;
                    {getCurrentMacInfo()?.version
                      ? t('download.versions.version', { version: getCurrentMacInfo()?.version })
                      : ''}
                  </span>
                  <img
                    src={arrowPng}
                    alt="Dropdown arrow"
                    className={`${styles.buttonIcon} ${showDropdown ? styles.rotated : ''}`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Windows 卡片 */}
          <div className={`${styles.downloadCard} ${styles.windowsCard}`}>
            {/* 背景图 */}
            <div className={styles.cardBackground}>
              <img src={bgImg} alt="Windows background" className={styles.backgroundImage} />
            </div>

            {/* 卡片内容 */}
            <div className={styles.cardContentWrapper}>
              {/* 平台信息 */}
              <div className={styles.platformSection}>
                <div className={styles.platformIconWrapper}>
                  <img src={windowImg} alt="Windows icon" className={styles.platformIcon} />
                </div>
                <div className={styles.platformInfo}>
                  <h3 className={styles.platformTitle}>{t('download.platforms.windows')}</h3>
                  <p className={styles.platformRequirement}>
                    {t('download.platforms.winRequirement')}
                  </p>
                </div>
              </div>

              {/* Windows 下载按钮 */}
              <div className={styles.downloadButtonGroup}>
                <button
                  className={`${styles.downloadButton} ${styles.windows} ${styles.standalone}`}
                  onClick={() => handleDownload('windows')}
                  disabled={loading || !downloadInfo?.windows?.url}
                >
                  <span className={styles.buttonText}>
                    {downloadInfo?.windows?.url
                      ? `${t('download.buttons.downloadFor', { platform: t('download.platforms.windows') })} ${
                          downloadInfo.windows.version
                            ? t('download.versions.version', {
                                version: downloadInfo.windows.version,
                              })
                            : ''
                        }`
                      : t('download.buttons.comingSoon')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 下拉菜单 - 使用Portal挂载到body */}
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.macDropdownMenu}
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 9999,
            }}
          >
            <button
              className={`${styles.macDropdownItem} ${selectedMacType === 'arm' ? styles.active : ''}`}
              onClick={() => {
                setSelectedMacType('arm');
                handleDownload('arm');
                setShowDropdown(false);
              }}
              disabled={loading || !downloadInfo?.arm?.url}
              style={{
                width: dropdownPosition.width - 16, // 减去padding
              }}
            >
              <span className={styles.dropdownItemText}>
                {t('download.buttons.downloadForAppleSilicon')}&nbsp;
                {downloadInfo?.arm?.version
                  ? t('download.versions.version', { version: downloadInfo.arm.version })
                  : ''}
              </span>
            </button>
            <button
              className={`${styles.macDropdownItem} ${selectedMacType === 'intel' ? styles.active : ''}`}
              onClick={() => {
                setSelectedMacType('intel');
                handleDownload('intel');
                setShowDropdown(false);
              }}
              disabled={loading || !downloadInfo?.intel?.url}
              style={{
                width: dropdownPosition.width - 16, // 减去padding
              }}
            >
              <span className={styles.dropdownItemText}>
                {t('download.buttons.downloadForIntel')}&nbsp;
                {downloadInfo?.intel?.version
                  ? t('download.versions.version', { version: downloadInfo.intel.version })
                  : ''}
              </span>
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DownloadSection;
